import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@components/common/button';
import styles from './styles.module.scss';
import TransactionTable from '@components/transactions_table';
import { useLocales } from '@hooks/helpers/useLocales';
import {
    ETransactionType,
    TTransactionLite,
    TTransactionsFilter
} from '@xeppt/xeppt-sdk/types/transaction';
import { TTransaction } from '@xeppt/xeppt-sdk/types';
import { formatDate } from '@utils/index';
import { useUserContext } from '@hooks/context/useUserContext';
import {
    transactionsActionsFilterArray,
    transactionsMethodsFilterArray
} from '@const/transactions';
import Chips from '@components/common/chips';
import Tabs from '@components/tabs';
import { TPaymentIntent } from '@xeppt/xeppt-sdk/types/payment-intent';
import Input from '@components/common/input';

interface IProps {
    transactions: TTransactionLite[] | TPaymentIntent[];
    isTransactionsLoading?: boolean;
    toggleFilter: () => void;
    refetchTransactions: () => void;
    triggerClearFilter: () => void;
    activeTab?: 'transactions' | 'requests';
    filter: TTransactionsFilter;
    onChangeTab?: (val: 'transactions' | 'requests') => void;
    onChangeFilter: (val: TTransactionsFilter) => void;
    search?: string;
    onSearch?: (val: string) => void;
    showDownload?: boolean;
    isSale?: boolean;
    onTransactionClick?: (transaction: TTransaction) => void;
}

const TableSection: FC<IProps> = ({
    transactions,
    toggleFilter,
    onChangeFilter,
    isTransactionsLoading,
    refetchTransactions,
    triggerClearFilter,
    onChangeTab,
    activeTab,
    filter,
    search,
    onSearch,
    showDownload,
    isSale,
    onTransactionClick
}) => {
    const { t, i18n } = useTranslation('sections', {
        keyPrefix: 'transactions.table'
    });
    const { t: transactionItemLocale } = useTranslation('components', {
        keyPrefix: 'transaction_tables.transaction_item'
    });
    const { user } = useUserContext();
    const { submitLocale } = useLocales();

    // const handleDownloadTransaction = () => {
    //     const formatTransaction = transactions.map((item) => {
    //         if (!isIntentPayment(item)) {
    //             ({
    //                 'Transaction ID': item.id,
    //                 Name: item.consumer_name,
    //                 'Consumer Email': item.consumer_email,
    //                 Action: getTransactionAction(
    //                     item.action,
    //                     item.method,
    //                     item.type,
    //                     transactionItemLocale
    //                 ),
    //                 Amount: item.type === ETransactionType.CREDIT ? item.amount * -1 : item.amount,
    //                 Currency: item.currency,
    //                 'Date ': `${formatDate(item.created_at, 'MM/DD/YYYY HH:mm:ss')} (${user?.profile?.timezone})`,
    //                 Method: enumTranslate[item.method][i18n.language as 'en' | 'fr'],
    //                 Status: ETransactionStatusTranslate[item.status][i18n.language as 'en' | 'fr'],
    //                 'Is Automatic': item.automatic ? 'Yes' : 'No'
    //             });
    //         }
    //     });
    //     downloadFileFromBlob(formatDataToXlsx(formatTransaction), 'all-transactions.xlsx');
    // };

    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (filter?.action?.length) count += 1;
        if (filter?.method?.length) count += 1;
        if (filter?.type?.length) count += 1;
        if (filter?.date_from && filter?.date_to) count += 1;
        if (filter?.status?.length) count += 1;
        if (filter?.order === 'ASC') count += 1;
        return count;
    }, [filter]);

    const isFilterActive = activeFilterCount > 0;

    return (
        <div className={styles.wrapper}>
            <div className={styles.table_header}>
                {(activeTab && onChangeTab) || (search !== undefined && onSearch) ? (
                    <div className={styles.header_left}>
                        {activeTab && onChangeTab && (
                            <Tabs
                                onChange={onChangeTab}
                                tabs={[
                                    {
                                        label: 'Transactions',
                                        value: 'transactions'
                                    },
                                    {
                                        label: 'External requests',
                                        value: 'requests'
                                    }
                                ]}
                                activeTab={activeTab}
                            />
                        )}
                        {search !== undefined && onSearch && activeTab !== 'requests' && (
                            <Input
                                placeholder="Search by customer, order ID, or reference code..."
                                leftIcon="search"
                                value={search}
                                onChange={onSearch}
                                className={styles.search}
                            />
                        )}
                    </div>
                ) : (
                    <div />
                )}
                <div />
                <div className={styles.right}>
                    {showDownload && (
                        <Button
                            size="medium"
                            variant="outlined-primary"
                            leftIcon="download"
                            onClick={() => {}}>
                            <p>{submitLocale('download')}</p>
                        </Button>
                    )}
                    <Button
                        variant="primary"
                        size="medium"
                        leftIcon="filter"
                        onClick={toggleFilter}>
                        <p>Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}</p>
                    </Button>
                </div>
            </div>
            <div className={styles.content}>
                {isFilterActive && (
                    <div className={styles.active_filters}>
                        {filter?.action?.map((item) => {
                            return (
                                <Chips
                                    key={item}
                                    label={
                                        transactionsActionsFilterArray(t).find(
                                            (action) => action.action === item
                                        )?.label || ''
                                    }
                                    onClose={() =>
                                        onChangeFilter({
                                            ...filter,
                                            action: filter?.action?.filter((type) => type !== item)
                                        })
                                    }
                                />
                            );
                        })}
                        {filter?.method?.map((item) => {
                            return (
                                <Chips
                                    key={item}
                                    label={
                                        transactionsMethodsFilterArray(t).find(
                                            (method) => method.method === item
                                        )?.label || ''
                                    }
                                    onClose={() =>
                                        onChangeFilter({
                                            ...filter,
                                            method: filter?.method?.filter((type) => type !== item)
                                        })
                                    }
                                />
                            );
                        })}
                        {filter?.type?.map((item) => {
                            return (
                                <Chips
                                    key={item}
                                    label={
                                        item === ETransactionType.CREDIT ? 'Outgoing' : 'Incoming'
                                    }
                                    onClose={() =>
                                        onChangeFilter({
                                            ...filter,
                                            type: filter?.type?.filter((type) => type !== item)
                                        })
                                    }
                                />
                            );
                        })}
                        {filter?.date_from && filter?.date_to && (
                            <Chips
                                label={`${formatDate(filter.date_from, 'MM/DD/YYYY')} - ${formatDate(filter.date_to, 'MM/DD/YYYY')}`}
                                onClose={() =>
                                    onChangeFilter({
                                        ...filter,
                                        date_to: undefined,
                                        date_from: undefined
                                    })
                                }
                            />
                        )}
                        <Button
                            variant="text-dark"
                            className={styles.clear_all}
                            onClick={() => {
                                onChangeFilter({});
                                triggerClearFilter();
                            }}>
                            {submitLocale('clear_all')}
                        </Button>
                    </div>
                )}
                <TransactionTable
                    activeTab={activeTab}
                    isTransactionsLoading={isTransactionsLoading}
                    transactions={transactions}
                    refetchTransactions={refetchTransactions}
                    isSale={isSale}
                    onTransactionClick={onTransactionClick}
                />
            </div>
        </div>
    );
};

export default TableSection;
